import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarX, ClipboardPlus, Clock3, FileText, HardHat, Loader2, Package, Paperclip, Plus, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import NuovaLavorazioneButton from "@/components/NuovaLavorazioneButton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const oggi = () => { const adesso = new Date(); const locale = new Date(adesso.getTime() - adesso.getTimezoneOffset() * 60000); return locale.toISOString().slice(0, 10); };
const dataLocale = (data) => {
  const [anno, mese, giorno] = String(data || "").split("-").map(Number);
  return anno && mese && giorno ? new Date(anno, mese - 1, giorno) : null;
};
const dataPasqua = (anno) => {
  const a = anno % 19; const b = Math.floor(anno / 100); const c = anno % 100;
  const d = Math.floor(b / 4); const e = b % 4; const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3); const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4); const k = c % 4; const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mese = Math.floor((h + l - 7 * m + 114) / 31);
  const giorno = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(anno, mese - 1, giorno);
};
const motivoDataNonLavorativa = (data) => {
  const valore = dataLocale(data);
  if (!valore) return "";
  if (valore.getDay() === 0) return "La domenica non è selezionabile";
  const giornoMese = `${String(valore.getMonth() + 1).padStart(2, "0")}-${String(valore.getDate()).padStart(2, "0")}`;
  const festivita = new Set(["01-01", "01-06", "04-25", "05-01", "06-02", "08-15", "11-01", "12-08", "12-25", "12-26"]);
  const pasquetta = dataPasqua(valore.getFullYear()); pasquetta.setDate(pasquetta.getDate() + 1);
  if (festivita.has(giornoMese) || valore.toDateString() === pasquetta.toDateString()) return "I giorni festivi non sono selezionabili";
  return "";
};
const ultimaDataLavorativa = () => {
  const valore = dataLocale(oggi());
  while (motivoDataNonLavorativa(`${valore.getFullYear()}-${String(valore.getMonth() + 1).padStart(2, "0")}-${String(valore.getDate()).padStart(2, "0")}`)) valore.setDate(valore.getDate() - 1);
  return `${valore.getFullYear()}-${String(valore.getMonth() + 1).padStart(2, "0")}-${String(valore.getDate()).padStart(2, "0")}`;
};
const nuovaQuota = () => ({ cantiere_id: "", lavorazione_id: "", quantita: "" });
const numero = (valore) => Number(String(valore ?? "").replace(",", ".")) || 0;
const nomeOperaio = (x) => `${x.nome || ""} ${x.cognome || ""}`.trim();

export default function RegistroCaricoPage() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState("operai");
  const [data, setData] = useState(ultimaDataLavorativa());
  const [operai, setOperai] = useState([]);
  const [attrezzi, setAttrezzi] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [lavorazioni, setLavorazioni] = useState({});
  const [disponibilita, setDisponibilita] = useState({});
  const [selezionato, setSelezionato] = useState("");
  const [ricerca, setRicerca] = useState("");
  const [totale, setTotale] = useState("8");
  const [quote, setQuote] = useState([nuovaQuota()]);
  const [lavorazioneCreataDaSelezionare, setLavorazioneCreataDaSelezionare] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assente, setAssente] = useState(false);
  const [orarioDiverso, setOrarioDiverso] = useState(false);
  const [tipoAssenza, setTipoAssenza] = useState("assenza");
  const [noteAssenza, setNoteAssenza] = useState("");
  const [ddt, setDdt] = useState({ numero_ddt: "", nome: "", fornitore: "", iva_percentuale: "22", allegato_path: null, allegato_nome: null });

  useEffect(() => {
    Promise.all([
      api.get("/gestionale/operai"),
      api.get("/attrezzi-catalogo"),
      api.get("/gestionale/anagrafiche/fornitori", { params: { include_archived: false } }).catch(() => ({ data: [] })),
      api.get("/cantieri"),
    ]).then(async ([o, a, f, c]) => {
      setOperai(Array.isArray(o.data) ? o.data.filter((x) => !x.archived_at) : []);
      setAttrezzi(Array.isArray(a.data) ? a.data.filter((x) => !x.archived_at) : []);
      setFornitori(Array.isArray(f.data) ? f.data.filter((x) => !x.archived_at && x.nome).sort((x, y) => x.nome.localeCompare(y.nome, "it")) : []);
      const attivi = (Array.isArray(c.data) ? c.data : []).filter((x) => String(x.stato || "").toLowerCase() !== "completato");
      setCantieri(attivi);
      const risultati = await Promise.all(attivi.map((x) => api.get(`/cantieri/${x.id}/lavorazioni`).catch(() => ({ data: [] }))));
      setLavorazioni(Object.fromEntries(attivi.map((x, i) => [x.id, Array.isArray(risultati[i].data) ? risultati[i].data : []])));
    }).catch(() => toast.error("Impossibile caricare i dati del registro"));
  }, []);

  useEffect(() => {
    api.get("/manodopera/disponibilita", { params: { data } })
      .then((r) => setDisponibilita(r.data?.operai || {}))
      .catch(() => setDisponibilita({}));
  }, [data]);

  useEffect(() => {
    const selezione = lavorazioneCreataDaSelezionare;
    if (
      !selezione ||
      !(lavorazioni[selezione.cantiereId] || []).some(
        (voce) => voce.id === selezione.id
      )
    ) return;

    setQuote((correnti) => correnti.map((quota, indice) =>
      indice === selezione.indice
        ? { ...quota, lavorazione_id: selezione.id }
        : quota
    ));
    setLavorazioneCreataDaSelezionare(null);
  }, [lavorazioneCreataDaSelezionare, lavorazioni]);

  const operaio = operai.find((x) => x.id === selezionato);
  const attrezzo = attrezzi.find((x) => x.id === selezionato);
  const disponibileOperaio = operaio ? Number(disponibilita[operaio.id]?.ore_disponibili ?? 8) : 0;
  const sommaQuote = quote.reduce((s, x) => s + numero(x.quantita), 0);
  const residuo = Math.round((numero(totale) - sommaQuote) * 100) / 100;

  const elenco = useMemo(() => {
    const needle = ricerca.trim().toLocaleLowerCase("it");
    const base = tipo === "operai" ? operai : attrezzi;
    return base.filter((x) => {
      const testo = tipo === "operai" ? `${nomeOperaio(x)} ${x.azienda_nome || ""} ${x.ruolo || ""}` : `${x.nome || ""} ${x.marca || ""} ${x.modello || ""} ${x.targa || ""}`;
      return (!needle || testo.toLocaleLowerCase("it").includes(needle))
        && (tipo !== "operai" || orarioDiverso || Number(disponibilita[x.id]?.ore_disponibili ?? 8) > 0);
    });
  }, [tipo, ricerca, operai, attrezzi, disponibilita, orarioDiverso]);

  const cambiaTipo = (nuovo) => {
    setTipo(nuovo); setSelezionato(""); setRicerca(""); setQuote([nuovaQuota()]); setAssente(false); setOrarioDiverso(false);
    setTotale(nuovo === "materiali" ? "" : "8");
  };

  const seleziona = (id) => {
    setSelezionato(id);
    setAssente(false); setOrarioDiverso(false); setTipoAssenza("assenza"); setNoteAssenza("");
    const valore = tipo === "operai" ? Number(disponibilita[id]?.ore_disponibili ?? 8) : 8;
    setTotale(String(valore));
    setQuote([{ ...nuovaQuota(), quantita: String(valore) }]);
  };

  const aggiornaQuota = (indice, campo, valore) => setQuote((correnti) => correnti.map((q, i) => i === indice
    ? { ...q, [campo]: valore, ...(campo === "cantiere_id" ? { lavorazione_id: "" } : {}) }
    : q));

  const aggiungiQuota = () => setQuote((x) => [...x, nuovaQuota()]);
  const eliminaQuota = (indice) => setQuote((x) => x.length === 1 ? x : x.filter((_, i) => i !== indice));

  const lavorazioneCreata = (indice, cantiereId, nuova) => {
    if (!nuova?.id) return;
    setLavorazioni((correnti) => ({
      ...correnti,
      [cantiereId]: [
        ...(correnti[cantiereId] || []).filter((voce) => voce.id !== nuova.id),
        nuova,
      ],
    }));
    setLavorazioneCreataDaSelezionare({ indice, cantiereId, id: nuova.id });
  };

  const uploadBolla = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data: risposta } = await api.post("/uploads/bolla", form, { headers: { "Content-Type": "multipart/form-data" } });
      setDdt((x) => ({ ...x, allegato_path: risposta.path, allegato_nome: risposta.nome }));
      toast.success("Bolla caricata");
    } catch { toast.error("Errore nel caricamento della bolla"); }
    finally { setUploading(false); }
  };

  const valida = () => {
    if (!data || data > oggi()) return "Non puoi registrare carichi con una data successiva a oggi";
    if (tipo === "operai" && motivoDataNonLavorativa(data)) return motivoDataNonLavorativa(data);
    if (tipo !== "materiali" && !selezionato) return "Seleziona una voce da caricare";
    if (tipo === "operai" && assente) return "";
    if (tipo === "materiali" && (!ddt.nome.trim() || !ddt.numero_ddt.trim() || !ddt.fornitore.trim())) return "Inserisci descrizione, numero DDT e fornitore";
    if (numero(totale) <= 0) return "Inserisci un totale maggiore di zero";
    if (tipo === "operai" && !orarioDiverso && numero(totale) > disponibileOperaio) return `L'operaio ha solo ${disponibileOperaio} ore disponibili`;
    if (quote.some((x) => !x.cantiere_id || numero(x.quantita) <= 0)) return "Completa cantiere e quantità di ogni quota";
    if (Math.abs(residuo) > 0.001) return `La ripartizione non coincide con il totale. Residuo: ${residuo}`;
    return "";
  };

  const salva = async () => {
    const errore = valida();
    if (errore) { toast.error(errore); return; }
    setSaving(true);
    const creati = [];
    try {
      if (tipo === "operai" && assente) {
        await api.post(`/gestionale/operai/${operaio.id}/assenze`, null, {
          params: { data, tipo: tipoAssenza, note: noteAssenza.trim() },
        });
        toast.success("Assenza registrata nella Mensilità dell'operaio");
        const r = await api.get("/manodopera/disponibilita", { params: { data } });
        setDisponibilita(r.data?.operai || {});
        setSelezionato(""); setAssente(false); setTipoAssenza("assenza"); setNoteAssenza("");
        return;
      }
      for (const quota of quote) {
        if (tipo === "operai") {
          const response = await api.post(`/cantieri/${quota.cantiere_id}/manodopera`, {
            operaio: nomeOperaio(operaio), operaio_id: operaio.id, azienda: operaio.azienda_nome || "",
            ruolo: operaio.ruolo || "", data, ore: numero(quota.quantita), costo_orario: numero(operaio.costo_orario),
            lavorazione_id: quota.lavorazione_id || null, orario_diverso: orarioDiverso,
          });
          creati.push(["manodopera", response.data.id]);
        } else if (tipo === "materiali") {
          const response = await api.post(`/cantieri/${quota.cantiere_id}/materiali`, {
            ...ddt, data_acquisto: data, costo_totale: numero(quota.quantita),
            iva_percentuale: numero(ddt.iva_percentuale), lavorazione_id: quota.lavorazione_id || null,
          });
          creati.push(["materiali", response.data.id]);
        } else {
          const response = await api.post(`/cantieri/${quota.cantiere_id}/attrezzi`, {
            catalogo_id: attrezzo.id, data_utilizzo: data, ore_utilizzate: numero(quota.quantita),
            lavorazione_id: quota.lavorazione_id || null,
          });
          creati.push(["attrezzi", response.data.id]);
        }
      }
      toast.success("Carico registrato nei cantieri selezionati");
      if (tipo === "operai") {
        const r = await api.get("/manodopera/disponibilita", { params: { data } });
        setDisponibilita(r.data?.operai || {});
      }
      setSelezionato(""); setQuote([nuovaQuota()]); setTotale(tipo === "materiali" ? "" : "8"); setOrarioDiverso(false);
      if (tipo === "materiali") setDdt({ numero_ddt: "", nome: "", fornitore: "", iva_percentuale: "22", allegato_path: null, allegato_nome: null });
    } catch (err) {
      await Promise.allSettled(creati.map(([sezione, id]) => api.delete(`/${sezione}/${id}`)));
      toast.error(formatApiError(err.response?.data?.detail || "Salvataggio non riuscito"));
    } finally { setSaving(false); }
  };

  const sorgentePronta = tipo === "materiali" ? true : Boolean(selezionato);

  return <AppShell>
    <button onClick={() => navigate("/gestionale")} className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary"><ArrowLeft className="h-4 w-4"/>Gestionale</button>
    <div className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div><p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground"><ClipboardPlus className="h-4 w-4"/>Registrazione centralizzata</p><h1 className="mt-2 font-heading text-4xl font-bold tracking-tighter sm:text-5xl">Registro di carico</h1><p className="mt-2 text-muted-foreground">Attribuisci operai, DDT, materiali e attrezzi ai cantieri e alle lavorazioni.</p></div>
      <div className="w-full sm:w-48"><Label>Data del carico</Label><Input type="date" max={oggi()} value={data} onChange={(e) => { const nuovaData = e.target.value; const errore = tipo === "operai" ? motivoDataNonLavorativa(nuovaData) : ""; if (errore) { toast.error(errore); return; } setData(nuovaData); }} className="mt-2 h-11"/></div>
    </div>

    <Tabs value={tipo} onValueChange={cambiaTipo} className="mb-6">
      <TabsList className="grid h-auto w-full grid-cols-3 sm:w-auto">
        <TabsTrigger value="operai" className="gap-2 py-2.5"><HardHat className="h-4 w-4"/>Operai</TabsTrigger>
        <TabsTrigger value="materiali" className="gap-2 py-2.5"><FileText className="h-4 w-4"/>DDT e materiali</TabsTrigger>
        <TabsTrigger value="attrezzi" className="gap-2 py-2.5"><Wrench className="h-4 w-4"/>Attrezzi</TabsTrigger>
      </TabsList>
    </Tabs>

    <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
      <section className="rounded-xl border bg-card p-5">
        {tipo === "materiali" ? <>
          <h2 className="mb-4 font-heading text-xl font-bold">Nuovo DDT / materiale</h2>
          <div className="space-y-4">
            <div><Label>Numero DDT *</Label><Input value={ddt.numero_ddt} onChange={(e) => setDdt((x) => ({ ...x, numero_ddt: e.target.value }))} className="mt-1.5" placeholder="Es. 184/2026"/></div>
            <div><Label>Descrizione materiale *</Label><Input value={ddt.nome} onChange={(e) => setDdt((x) => ({ ...x, nome: e.target.value }))} className="mt-1.5" placeholder="Materiali riportati nel DDT"/></div>
            <div className="space-y-2">
              <Label>Fornitore *</Label>
              <SearchableCombobox
                value={ddt.fornitore}
                onValueChange={(value) => setDdt((x) => ({ ...x, fornitore: value }))}
                options={fornitori.map((x) => ({ value: x.nome, label: x.nome }))}
                placeholder="Seleziona il fornitore"
                searchPlaceholder="Scrivi il fornitore..."
                emptyText="Nessun fornitore trovato"
                testId="registro-carico-fornitore-select"
              />
              {fornitori.length === 0 && <p className="text-xs text-muted-foreground">Inserisci prima i fornitori nel Gestionale.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3"><div><Label>Imponibile totale *</Label><Input type="number" min="0" step="0.01" value={totale} onChange={(e) => { setTotale(e.target.value); if (quote.length === 1) aggiornaQuota(0, "quantita", e.target.value); }} className="mt-1.5"/></div><div><Label>IVA %</Label><Input type="number" min="0" step="0.1" value={ddt.iva_percentuale} onChange={(e) => setDdt((x) => ({ ...x, iva_percentuale: e.target.value }))} className="mt-1.5"/></div></div>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed p-3 text-sm font-medium hover:border-primary hover:text-primary"><Paperclip className="h-4 w-4"/>{uploading ? "Caricamento…" : ddt.allegato_nome || "Allega foto o PDF del DDT"}<input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadBolla(e.target.files?.[0])}/></label>
          </div>
        </> : <>
          <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-bold">{tipo === "operai" ? "Operai disponibili" : "Attrezzi disponibili"}</h2><span className="text-xs text-muted-foreground">{elenco.length} voci</span></div>
          <Input value={ricerca} onChange={(e) => setRicerca(e.target.value)} placeholder={tipo === "operai" ? "Cerca operaio…" : "Cerca attrezzo…"} className="mb-3"/>
          <div className="max-h-[470px] space-y-2 overflow-y-auto pr-1">{elenco.map((x) => {
            const isOperaio = tipo === "operai"; const disp = isOperaio ? Number(disponibilita[x.id]?.ore_disponibili ?? 8) : null; const disabilitato = isOperaio && !orarioDiverso && disp <= 0;
            return <button key={x.id} disabled={disabilitato} onClick={() => seleziona(x.id)} className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition ${selezionato === x.id ? "border-primary bg-primary/5" : "hover:border-primary/60"} ${disabilitato ? "cursor-not-allowed opacity-50" : ""}`}><span><strong className="block text-sm">{isOperaio ? nomeOperaio(x) : x.nome}</strong><small className="text-muted-foreground">{isOperaio ? `${x.ruolo || "Ruolo non indicato"} · ${x.azienda_nome || "Azienda non indicata"}` : [x.marca, x.modello, x.targa].filter(Boolean).join(" · ") || "Nessun dettaglio"}</small></span><span className="text-xs font-bold text-primary">{isOperaio ? `${disp} h` : `${numero(x.costo_orario).toFixed(2)} €/h`}</span></button>;
          })}</div>
        </>}
      </section>

      <section className="rounded-xl border bg-card p-5">
        <div className="mb-5 flex items-start justify-between gap-3"><div><h2 className="font-heading text-xl font-bold">{tipo === "operai" && assente ? "Registrazione assenza" : "Ripartizione nei cantieri"}</h2><p className="mt-1 text-sm text-muted-foreground">{tipo === "operai" && assente ? "L'assenza comparirà nella Mensilità senza ore e costi." : "Ogni quota può essere collegata a una lavorazione diversa."}</p></div>{tipo === "operai" && assente ? <CalendarX className="h-6 w-6 text-orange-500"/> : <Package className="h-6 w-6 text-primary"/>}</div>
        {!sorgentePronta ? <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">{tipo === "materiali" ? "Compila numero e descrizione del DDT." : `Seleziona ${tipo === "operai" ? "un operaio" : "un attrezzo"} dall'elenco.`}</div> : <>
          {tipo === "operai" && <div className="mb-4 grid gap-3 sm:grid-cols-2"><div className="flex items-center justify-between gap-4 rounded-lg border border-orange-200 bg-orange-50/60 p-4"><div><p className="font-semibold text-orange-800">Operaio assente</p><p className="mt-1 text-xs text-orange-700">Non richiede cantiere, lavorazione o ore.</p></div><Switch checked={assente} onCheckedChange={(valore)=>{setAssente(valore);if(valore)setOrarioDiverso(false)}} aria-label="Operaio assente"/></div><Button type="button" variant={orarioDiverso ? "default" : "outline"} className="h-auto min-h-[78px] justify-start px-4 text-left" onClick={()=>{setOrarioDiverso((attivo)=>{const nuovo=!attivo;if(nuovo&&quote.length===1)aggiornaQuota(0,"quantita",totale);return nuovo});setAssente(false)}}><Clock3 className="mr-3 h-5 w-5 shrink-0"/><span><span className="block font-semibold">Orario diverso</span><span className="mt-1 block text-xs font-normal opacity-80">Consente di registrare meno o più di 8 ore.</span></span></Button></div>}
          {tipo === "operai" && assente ? <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Tipo assenza</Label><Select value={tipoAssenza} onValueChange={setTipoAssenza}><SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger><SelectContent><SelectItem value="assenza">Assenza</SelectItem><SelectItem value="ferie">Ferie</SelectItem><SelectItem value="malattia">Malattia</SelectItem><SelectItem value="permesso">Permesso</SelectItem><SelectItem value="infortunio">Infortunio</SelectItem></SelectContent></Select></div>
              <div><Label>Nota facoltativa</Label><Input value={noteAssenza} onChange={(e) => setNoteAssenza(e.target.value)} placeholder="Motivo o annotazione…" className="mt-1.5"/></div>
            </div>
            <div className="mt-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">La giornata sarà conteggiata esclusivamente tra le assenze e l'operaio scomparirà dai disponibili per questa data.</div>
            <div className="mt-4 flex justify-end"><Button type="button" onClick={salva} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <CalendarX className="mr-2 h-4 w-4"/>}Salva assenza</Button></div>
          </> : <>
          <div className="mb-4 grid grid-cols-2 gap-3 rounded-lg bg-muted/50 p-3"><div><span className="text-xs text-muted-foreground">Totale da ripartire</span><Input type="number" min="0" step="0.5" value={totale} onChange={(e) => { const valore=e.target.value; setTotale(valore); if (quote.length === 1) aggiornaQuota(0, "quantita", valore); }} className="mt-1 bg-background"/></div><div><span className="text-xs text-muted-foreground">{tipo === "operai" && orarioDiverso ? "Ore assegnate" : "Residuo"}</span><div className={`mt-1 flex h-10 items-center rounded-md px-3 font-bold ${Math.abs(residuo) < 0.001 ? "bg-emerald-50 text-emerald-700" : "bg-orange-50 text-orange-700"}`}>{tipo === "operai" && orarioDiverso ? sommaQuote : residuo} {tipo === "materiali" ? "€" : "ore"}</div></div></div>
          <div className="space-y-3">{quote.map((q, indice) => <div key={indice} className="grid gap-2 rounded-lg border p-3 sm:grid-cols-[1fr_1fr_110px_36px]">
            <SearchableCombobox value={q.cantiere_id} onValueChange={(v) => aggiornaQuota(indice, "cantiere_id", v)} options={cantieri.map((x) => ({ value: x.id, label: x.nome }))} placeholder="Seleziona cantiere" searchPlaceholder="Cerca cantiere…" emptyText="Nessun cantiere"/>
            <div className="space-y-2"><SearchableCombobox value={q.lavorazione_id} onValueChange={(v) => aggiornaQuota(indice, "lavorazione_id", v === "__none__" ? "" : v)} options={[{ value: "__none__", label: "Nessuna lavorazione" }, ...(lavorazioni[q.cantiere_id] || []).map((x) => ({ value: x.id, label: x.nome }))]} placeholder="Lavorazione" searchPlaceholder="Cerca lavorazione…" emptyText="Nessuna lavorazione"/><NuovaLavorazioneButton cantiereId={q.cantiere_id} onCreated={(nuova) => lavorazioneCreata(indice, q.cantiere_id, nuova)} className="w-full"/></div>
            <Input type="number" min="0" step={tipo === "materiali" ? "0.01" : "0.5"} value={q.quantita} onChange={(e) => aggiornaQuota(indice, "quantita", e.target.value)} aria-label={tipo === "materiali" ? "Importo quota" : "Ore quota"}/>
            <Button type="button" variant="ghost" size="icon" onClick={() => eliminaQuota(indice)} disabled={quote.length === 1} className="text-destructive"><Trash2 className="h-4 w-4"/></Button>
          </div>)}</div>
          <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between"><Button type="button" variant="outline" onClick={aggiungiQuota}><Plus className="mr-2 h-4 w-4"/>Aggiungi quota</Button><Button type="button" onClick={salva} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <ClipboardPlus className="mr-2 h-4 w-4"/>}Salva carico</Button></div>
          </>}
        </>}
      </section>
    </div>
  </AppShell>;
}
